import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { UserLanguageSync } from '../auth/UserLanguageSync';

import { useLanguage } from '../../hooks/useLanguage';
import { IS_TENANT_MODE } from '../../config/tenantMode';
import { isAuthenticated } from '../../utils/tokenManager';

import { Footer } from './Footer';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { AdminNoticesBanner } from '../admin/AdminNoticesBanner';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { isRTL } = useLanguage();
  const location = useLocation();
  const auth = isAuthenticated();
  const isTenantLoginPage = IS_TENANT_MODE && !auth && location.pathname === '/login';
  const showSidebar = auth;

  // When sidebar closes on mobile, return focus to menu button so focus is not inside inert/hidden content
  useEffect(() => {
    if (!isSidebarOpen && !isDesktop) {
      menuButtonRef.current?.focus({ preventScroll: true });
    }
  }, [isSidebarOpen, isDesktop]);

  useEffect(() => {
    const checkScreenSize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (!desktop) {
        setIsSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isDesktop]);

  const handleMenuToggle = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  if (isTenantLoginPage) {
    return (
      <div className="tenant-shell min-h-screen flex flex-col overflow-x-hidden">
        <div className="tenant-ambient" aria-hidden="true" />
        <main className="flex-1 relative z-[1] px-4 py-8 sm:py-12" role="main">
          <div className="animate-slide-up mx-auto w-full max-w-md">{children}</div>
        </main>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex flex-col overflow-x-hidden ${
        IS_TENANT_MODE ? 'tenant-shell' : 'bg-brand-primary'
      }`}
    >
      {IS_TENANT_MODE ? <div className="tenant-ambient" aria-hidden="true" /> : null}
      {auth ? <UserLanguageSync /> : null}
      <Header
        onMenuToggle={showSidebar ? handleMenuToggle : undefined}
        isMenuOpen={isSidebarOpen}
        menuButtonRef={showSidebar ? menuButtonRef : undefined}
      />

      <div className="flex flex-1 relative overflow-x-hidden pt-20 md:pt-24">
        {showSidebar && <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />}

        <main
          className={`flex-1 transition-[margin] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] min-w-0 ${
            showSidebar && isSidebarOpen ? (isRTL ? 'lg:mr-64' : 'lg:ml-64') : ''
          }`}
          role="main"
        >
          <div
            key={location.pathname}
            className={`animate-slide-up mx-auto px-3.5 sm:px-5 md:px-6 py-5 sm:py-7 md:py-8 ${
              showSidebar && isSidebarOpen ? 'max-w-7xl' : 'max-w-6xl'
            }`}
          >
            {IS_TENANT_MODE && showSidebar ? <AdminNoticesBanner /> : null}
            {children}
          </div>
        </main>
      </div>

      {!auth && !IS_TENANT_MODE && <Footer />}
    </div>
  );
}
